<template>
  <v-menu
    v-model="menu"
    :close-on-content-click="false"
    location="bottom"
    max-width="400"
    offset="8"
  >
    <template v-slot:activator="{ props }">
      <v-btn icon variant="text" v-bind="props" class="mr-2">
        <v-badge
          :content="unreadCount"
          :model-value="unreadCount > 0"
          color="error"
          overlap
        >
          <v-icon>mdi-bell-outline</v-icon>
        </v-badge>
      </v-btn>
    </template>

    <v-card max-height="600" class="notification-panel">
      <v-card-title class="d-flex align-center pa-3 bg-primary">
        <span class="text-white">Notifications</span>
        <v-spacer></v-spacer>
        <v-btn
          v-if="unreadCount > 0"
          size="small"
          variant="text"
          color="white"
          @click="handleMarkAllRead"
        >
          Mark all read
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <!-- Tabs -->
      <v-tabs v-model="tab" grow>
        <v-tab value="all">All</v-tab>
        <v-tab value="unread">
          Unread
          <v-chip
            v-if="unreadCount > 0"
            size="x-small"
            color="error"
            class="ml-1"
          >
            {{ unreadCount }}
          </v-chip>
        </v-tab>
      </v-tabs>

      <v-divider></v-divider>

      <!-- Notification list -->
      <v-card-text class="pa-0" style="max-height: 450px; overflow-y: auto;">
        <v-progress-linear v-if="loading" indeterminate></v-progress-linear>

        <template v-if="!loading && displayedNotifications.length === 0">
          <div class="text-center pa-6">
            <v-icon size="64" color="grey-lighten-1">mdi-bell-off-outline</v-icon>
            <p class="text-body-2 text-medium-emphasis mt-2">
              {{ tab === 'unread' ? 'No unread notifications' : 'No notifications' }}
            </p>
          </div>
        </template>

        <v-list v-else lines="three" class="py-0">
          <template v-for="(notification, index) in displayedNotifications" :key="notification.id">
            <v-list-item
              :class="{ 'bg-blue-lighten-5': !notification.is_read }"
              @click="handleNotificationClick(notification)"
            >
              <template v-slot:prepend>
                <v-avatar :color="getPriorityColor(notification.priority)">
                  <v-icon color="white">{{ getTypeIcon(notification.type) }}</v-icon>
                </v-avatar>
              </template>

              <v-list-item-title class="font-weight-medium">
                {{ notification.title }}
              </v-list-item-title>
              <v-list-item-subtitle class="text-caption">
                {{ notification.message }}
              </v-list-item-subtitle>
              <v-list-item-subtitle class="text-caption text-medium-emphasis mt-1">
                {{ formatTime(notification.created_at) }}
              </v-list-item-subtitle>

              <template v-slot:append>
                <div class="d-flex flex-column align-center">
                  <v-chip
                    v-if="!notification.is_read"
                    size="x-small"
                    color="primary"
                    class="mb-1"
                  >
                    New
                  </v-chip>
                  <v-btn
                    icon
                    size="x-small"
                    variant="text"
                    @click.stop="handleDelete(notification.id)"
                  >
                    <v-icon size="small">mdi-close</v-icon>
                  </v-btn>
                </div>
              </template>
            </v-list-item>
            <v-divider v-if="index < displayedNotifications.length - 1"></v-divider>
          </template>
        </v-list>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="pa-2">
        <v-btn
          block
          variant="text"
          size="small"
          color="primary"
          @click="loadMore"
          :disabled="loading"
        >
          Load More
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useNotificationStore } from '@/stores/notification'
import type { Notification } from '@/services/notificationService'
import { formatRelativeTime } from '@/utils/dateFormatter'

const notificationStore = useNotificationStore()

const menu = ref(false)
const tab = ref('all')
const loading = computed(() => notificationStore.loading)
const notifications = computed(() => notificationStore.notifications)
const unreadCount = computed(() => notificationStore.unreadCount)

const displayedNotifications = computed(() => {
  if (tab.value === 'unread') {
    return notifications.value.filter(n => !n.is_read)
  }
  return notifications.value
})

function getPriorityColor(priority: string) {
  switch (priority) {
    case 'critical':
      return 'error'
    case 'high':
      return 'warning'
    case 'medium':
      return 'info'
    case 'low':
      return 'success'
    default:
      return 'grey'
  }
}

function getTypeIcon(type: string) {
  switch (type) {
    case 'water_quality':
      return 'mdi-water-alert'
    case 'feeding':
      return 'mdi-food-apple'
    case 'low_stock':
      return 'mdi-package-variant-closed'
    case 'harvest':
      return 'mdi-fish-off'
    case 'sampling':
      return 'mdi-clipboard-check'
    case 'system':
      return 'mdi-information'
    case 'alert':
      return 'mdi-alert'
    default:
      return 'mdi-bell'
  }
}

function formatTime(dateString: string) {
  return formatRelativeTime(dateString)
}

async function handleNotificationClick(notification: Notification) {
  if (!notification.is_read) {
    try {
      await notificationStore.markAsRead(notification.id)
    } catch (error) {
      notificationStore.showError('Failed to mark notification as read')
    }
  }
  
  // TODO: Navigate to related entity if needed
  // if (notification.related_entity_type && notification.related_entity_id) {
  //   router.push(`/${notification.related_entity_type}/${notification.related_entity_id}`)
  // }
}

async function handleMarkAllRead() {
  try {
    await notificationStore.markAllAsRead()
    notificationStore.showSuccess('All notifications marked as read')
  } catch (error) {
    notificationStore.showError('Failed to mark all as read')
  }
}

async function handleDelete(notificationId: string) {
  try {
    await notificationStore.deleteNotification(notificationId)
    notificationStore.showSuccess('Notification deleted')
  } catch (error) {
    notificationStore.showError('Failed to delete notification')
  }
}

async function loadMore() {
  // TODO: Implement pagination
  notificationStore.showInfo('Load more coming soon')
}

// Fetch notifications when menu opens
watch(menu, async (isOpen) => {
  if (isOpen) {
    await notificationStore.fetchNotifications()
  }
})

// Fetch unread count periodically
onMounted(async () => {
  await notificationStore.fetchUnreadCount()
  
  // Refresh unread count every 30 seconds
  setInterval(() => {
    notificationStore.fetchUnreadCount()
  }, 30000)
})
</script>

<style scoped>
.notification-panel {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.v-list-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
}
</style>
